import React, { useState } from 'react';
import {
  FileText,
  User,
  Briefcase,
  CheckCircle2,
  Sliders,
  Eye,
  Download,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Shield,
  Layers,
  Save
} from 'lucide-react';
import {
  PracticeReport,
  PracticeReportType,
  ReportStatus,
  PracticeReportConfig,
  PracticeClient,
  PracticeCase
} from '../../types/practiceManagement';
import { Language, CompleteKundali } from '../../types/kundali';
import { ReportPreview } from './ReportPreview';

interface ReportBuilderProps {
  clients: PracticeClient[];
  cases: PracticeCase[];
  initialReport?: PracticeReport | null;
  initialClient?: PracticeClient | null;
  initialCase?: PracticeCase | null;
  kundali?: CompleteKundali;
  lang: Language;
  onSaveReport: (report: PracticeReport) => void;
  onCancel: () => void;
}

const REPORT_TYPES: Array<{ type: PracticeReportType; titleEn: string; titleHi: string; descEn: string }> = [
  {
    type: 'EXECUTIVE_CLIENT_BRIEF',
    titleEn: 'Executive Client Brief',
    titleHi: 'संक्षिप्त क्लाइंट विवरण (2-3 पृष्ठ)',
    descEn: 'Clean, elegant summary with natal promise, primary timing windows, and practical remedies.'
  },
  {
    type: 'FULL_CLASSICAL_DOSSIER',
    titleEn: 'Full Classical Dossier',
    titleHi: 'विस्तृत शास्त्रीय महा-रिपोर्ट',
    descEn: 'Comprehensive 8+ page analysis covering Vargas, Shadbala, Ashtakavarga, and Dasha-Gochar proofs.'
  },
  {
    type: 'CONSULTATION_REPORT',
    titleEn: 'Consultation Session Report',
    titleHi: 'परामर्श सत्र प्रतिवेदन',
    descEn: 'Structured summary of consultation Q&A, guidance, and agreed action items.'
  },
  {
    type: 'TIMING_REPORT',
    titleEn: 'Phala-Bhuta Timing Radar Report',
    titleHi: 'समय काल व गोचर रिपोर्ट',
    descEn: 'Focused exclusively on high-confluence planetary time horizons over the next 12–24 months.'
  },
  {
    type: 'FORENSIC_RESEARCH_REPORT',
    titleEn: 'Forensic Research Dossier',
    titleHi: 'शोध एवं अनुसंधान डॉसियर',
    descEn: 'Technical report with raw planetary coordinates, mathematical matrices, and classical citations.'
  }
];

export const ReportBuilder: React.FC<ReportBuilderProps> = ({
  clients,
  cases,
  initialReport,
  initialClient,
  initialCase,
  kundali,
  lang,
  onSaveReport,
  onCancel
}) => {
  const isHi = lang === 'hi';
  const [step, setStep] = useState<'SELECT' | 'CUSTOMIZE' | 'PREVIEW'>('SELECT');

  // Selected state
  const [selectedClientId, setSelectedClientId] = useState<string>(
    initialReport?.clientId || initialClient?.id || (clients[0]?.id ?? '')
  );
  const [selectedCaseId, setSelectedCaseId] = useState<string>(
    initialReport?.caseId || initialCase?.id || (cases[0]?.id ?? '')
  );
  const [reportType, setReportType] = useState<PracticeReportType>(
    initialReport?.reportType || 'EXECUTIVE_CLIENT_BRIEF'
  );
  const [title, setTitle] = useState<string>(
    initialReport?.title || 'Vedic Life Assessment & Timing Dossier'
  );
  const [subtitle, setSubtitle] = useState<string>(
    initialReport?.subtitle || 'Comprehensive Jyotish Analysis & Phala-Bhuta Timing Windows'
  );
  const [status, setStatus] = useState<ReportStatus>(initialReport?.status || 'DRAFT');

  // Modular inclusions config
  const [config, setConfig] = useState<PracticeReportConfig>(
    initialReport?.config || {
      includeCover: true,
      includeBirthCoordinates: true,
      includeLifeStageContext: true,
      includeNatalPromise: true,
      includeD1Chart: true,
      includeThematicVargaChart: true,
      includeD9Chart: true,
      includeBhavaAnalysis: true,
      includePlanetaryStrengths: true,
      includeAshtakavargaMatrix: false,
      includeDashaGocharConfluence: true,
      includePhalaBhutaWindows: true,
      includeClassicalReasoning: true,
      includeRemedies: true,
      includePractitionerNotes: false,
      includeConsultationSummary: true,
      includeRawForensicTables: false
    }
  );

  const selectedClient = clients.find((c) => c.id === selectedClientId) || clients[0];
  const selectedCase = cases.find((c) => c.id === selectedCaseId) || cases[0];

  const toggleConfig = (key: keyof PracticeReportConfig) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreateOrUpdate = () => {
    const reportData: PracticeReport = {
      id: initialReport?.id || `rep_${Date.now()}`,
      reportNumber: initialReport?.reportNumber || `REP-2026-${Math.floor(100 + Math.random() * 900)}`,
      clientId: selectedClientId,
      clientRefId: selectedClient?.referenceId || 'CL-001',
      clientName: selectedClient?.name || 'Client',
      caseId: selectedCaseId,
      caseNumber: selectedCase?.caseNumber || 'CASE-001',
      themeKey: selectedCase?.themeKey || 'CAREER_BUSINESS',
      targetQuestion: selectedCase?.targetQuestion || 'What is the auspicious timing for my next venture?',
      reportType,
      title,
      subtitle,
      version: initialReport ? `${initialReport.version}.1` : '1.0',
      status,
      generatedBy: 'Acharya V. Sharma',
      practitionerCredentials: 'Principal Vedic Astrologer, Jyotish Visharad',
      generatedDate: new Date().toISOString().split('T')[0],
      lastModifiedDate: new Date().toISOString().split('T')[0],
      config,
      executiveSummaryText:
        selectedCase?.deterministicFindings?.natalPromiseVerdict ||
        'The natal chart confirms high capability for achievement and auspicious timing confluence.',
      timingVerdictText:
        selectedCase?.timingWindowsSummary ||
        'Prime fructification window spans Oct 2026 to Mar 2027 during Jupiter-Saturn Gochar confluence.',
      remediesText:
        selectedCase?.prescribedRemedies
          ?.map((r) => `• ${r.title} (${r.category}): ${r.description}`)
          .join('\n') || '• Brihaspati Puja and Gayatri Japa recommended.'
    };

    onSaveReport(reportData);
  };

  return (
    <div className="space-y-4">
      {/* Step Indicator Header */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted hover:bg-astro-card cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" />
            <h2 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {initialReport ? (isHi ? 'रिपोर्ट संपादन' : 'Edit Report') : (isHi ? 'नई रिपोर्ट तैयार करें' : 'Create Vedic Report')}
            </h2>
          </div>
        </div>

        {/* Step Tabs */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStep('SELECT')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              step === 'SELECT'
                ? 'bg-astro-secondary-surface0 text-white shadow-xs'
                : 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted'
            }`}
          >
            1. {isHi ? 'प्रारूप चयन' : 'Template & Client'}
          </button>

          <button
            onClick={() => setStep('CUSTOMIZE')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              step === 'CUSTOMIZE'
                ? 'bg-astro-secondary-surface0 text-white shadow-xs'
                : 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted'
            }`}
          >
            2. {isHi ? 'सामग्री अनुकूलन' : 'Modules & Customization'}
          </button>

          <button
            onClick={() => setStep('PREVIEW')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              step === 'PREVIEW'
                ? 'bg-astro-secondary-surface0 text-white shadow-xs'
                : 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted'
            }`}
          >
            3. {isHi ? 'पूर्वावलोकन व एक्सपोर्ट' : 'Preview & Export'}
          </button>
        </div>
      </div>

      {/* Step 1: SELECT */}
      {step === 'SELECT' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted">
              1. Select Client & Context Case
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text dark:text-astro-muted mb-1">
                  Client Profile:
                </label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="w-full p-2.5 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted font-bold"
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.referenceId}) • {c.lagnaSign} Lagna
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text dark:text-astro-muted mb-1">
                  Associated Case / Reading:
                </label>
                <select
                  value={selectedCaseId}
                  onChange={(e) => setSelectedCaseId(e.target.value)}
                  className="w-full p-2.5 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
                >
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.caseNumber} • {c.themeTitleEn} — "{c.targetQuestion.substring(0, 45)}..."
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text dark:text-astro-muted mb-1">
                  Report Title:
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-2.5 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text dark:text-astro-muted mb-1">
                  Subtitle:
                </label>
                <input
                  type="text"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  className="w-full p-2.5 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
                />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted">
              2. Select Report Template
            </h3>

            <div className="space-y-2.5">
              {REPORT_TYPES.map((t) => (
                <div
                  key={t.type}
                  onClick={() => setReportType(t.type)}
                  className={`p-3 rounded-xl border transition-all cursor-pointer ${
                    reportType === t.type
                      ? 'bg-astro-secondary-surface0/10 border-astro-accent dark:border-amber-400 text-astro-text dark:text-astro-muted shadow-xs'
                      : 'bg-astro-secondary-surface dark:bg-astro-primary border-astro-border dark:border-astro-strong-border text-astro-secondary-text dark:text-astro-muted hover:border-astro-border'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs">
                      {isHi ? t.titleHi : t.titleEn}
                    </span>
                    {reportType === t.type && (
                      <CheckCircle2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    )}
                  </div>
                  <p className="text-[11px] text-astro-muted dark:text-astro-muted pt-1 leading-normal">
                    {t.descEn}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setStep('CUSTOMIZE')}
                className="px-4 py-2 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <span>Continue to Customization</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: CUSTOMIZE */}
      {step === 'CUSTOMIZE' && (
        <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted">
              Module Inclusion & Layout Configuration
            </h3>
            <span className="text-xs text-astro-muted">
              Selected: {Object.values(config).filter(Boolean).length} modules enabled
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: 'includeCover' as keyof PracticeReportConfig, label: 'Official Letterhead Cover & Metadata' },
              { key: 'includeBirthCoordinates' as keyof PracticeReportConfig, label: 'Client Natal Coordinates & Age' },
              { key: 'includeLifeStageContext' as keyof PracticeReportConfig, label: 'Life-Stage Contextual Focus' },
              { key: 'includeNatalPromise' as keyof PracticeReportConfig, label: 'Natal Promise & Astrological Synthesis' },
              { key: 'includeD1Chart' as keyof PracticeReportConfig, label: 'Rashi (D1) Natal Diagram' },
              { key: 'includeD9Chart' as keyof PracticeReportConfig, label: 'Navamsha (D9) Chart Geometry' },
              { key: 'includeThematicVargaChart' as keyof PracticeReportConfig, label: 'Focal Thematic Varga Chart (e.g., D10)' },
              { key: 'includeBhavaAnalysis' as keyof PracticeReportConfig, label: 'Bhava Cusps & House Lord Evaluation' },
              { key: 'includePlanetaryStrengths' as keyof PracticeReportConfig, label: 'Shadbala & Planetary Dignities' },
              { key: 'includeAshtakavargaMatrix' as keyof PracticeReportConfig, label: 'Ashtakavarga Bindu Allocation' },
              { key: 'includeDashaGocharConfluence' as keyof PracticeReportConfig, label: 'Vimshottari Dasha & Gochar Matrix' },
              { key: 'includePhalaBhutaWindows' as keyof PracticeReportConfig, label: 'Phala-Bhuta Precise Timing Windows' },
              { key: 'includeClassicalReasoning' as keyof PracticeReportConfig, label: 'Classical Shastra Evidence & Citations' },
              { key: 'includeRemedies' as keyof PracticeReportConfig, label: 'Prescribed Classical Remedies & Mantras' },
              { key: 'includeConsultationSummary' as keyof PracticeReportConfig, label: 'Consultation Action Takeaways' },
              { key: 'includePractitionerNotes' as keyof PracticeReportConfig, label: 'Practitioner Confidential Notes (Private)' },
              { key: 'includeRawForensicTables' as keyof PracticeReportConfig, label: 'Raw Planetary Coordinate Table' }
            ].map((item) => (
              <label
                key={String(item.key)}
                className="flex items-center gap-2.5 p-3 rounded-lg bg-astro-secondary-surface dark:bg-astro-primary border border-astro-border dark:border-astro-strong-border text-xs font-semibold text-astro-text dark:text-astro-muted cursor-pointer select-none hover:border-amber-400"
              >
                <input
                  type="checkbox"
                  checked={!!config[item.key]}
                  onChange={() => toggleConfig(item.key)}
                  className="rounded text-amber-500 focus:ring-amber-500 h-4 w-4"
                />
                <span className="leading-snug">{item.label}</span>
              </label>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-astro-border dark:border-astro-strong-border">
            <button
              onClick={() => setStep('SELECT')}
              className="px-4 py-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted font-bold text-xs cursor-pointer"
            >
              Back
            </button>

            <button
              onClick={() => setStep('PREVIEW')}
              className="px-4 py-2 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <span>Generate & Preview Document</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: PREVIEW & EXPORT */}
      {step === 'PREVIEW' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-astro-secondary-text dark:text-astro-muted">
                Quality Status:
              </span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ReportStatus)}
                className="text-xs font-bold px-2.5 py-1 rounded bg-astro-secondary-surface dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border"
              >
                <option value="DRAFT">Draft</option>
                <option value="READY_FOR_REVIEW">Ready for Review</option>
                <option value="APPROVED">Approved</option>
                <option value="DELIVERED">Delivered</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setStep('CUSTOMIZE')}
                className="px-3.5 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted text-xs font-bold cursor-pointer"
              >
                Edit Modules
              </button>

              <button
                onClick={handleCreateOrUpdate}
                className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>Save to Repository</span>
              </button>
            </div>
          </div>

          <ReportPreview
            report={{
              id: initialReport?.id || 'temp-id',
              reportNumber: initialReport?.reportNumber || 'REP-2026-TEMP',
              clientId: selectedClientId,
              clientRefId: selectedClient?.referenceId || 'CL-001',
              clientName: selectedClient?.name || 'Client',
              caseId: selectedCaseId,
              caseNumber: selectedCase?.caseNumber || 'CASE-001',
              themeKey: selectedCase?.themeKey || 'CAREER_BUSINESS',
              targetQuestion: selectedCase?.targetQuestion || 'Question...',
              reportType,
              title,
              subtitle,
              version: '1.0',
              status,
              generatedBy: 'Acharya V. Sharma',
              practitionerCredentials: 'Principal Vedic Astrologer, Jyotish Visharad',
              generatedDate: new Date().toISOString().split('T')[0],
              lastModifiedDate: new Date().toISOString().split('T')[0],
              config,
              executiveSummaryText:
                selectedCase?.deterministicFindings?.natalPromiseVerdict ||
                'The chart indicates exceptional capability and resilience in fulfilling the inquiry objectives.',
              timingVerdictText:
                selectedCase?.timingWindowsSummary ||
                'Primary fructification window active during current Dasha and Jupiter-Saturn Gochar.',
              remediesText:
                selectedCase?.prescribedRemedies
                  ?.map((r) => `• ${r.title}: ${r.description}`)
                  .join('\n') || '• Brihaspati Stotram chanting and yellow sapphire.'
            }}
            kundali={kundali}
            lang={lang}
          />
        </div>
      )}
    </div>
  );
};
